import * as mobx from 'mobx';
import { IConnectionAttendeeDTO, IConnectionOrganizationDTO } from '../../../api/connections.dao';
import { ConnectionEntity } from '../abstract-connection.entity';
import { ConnectionEntityAggregate } from '../abstract-connection.entity.aggregate';

import { OrganizationRole } from '../types';
import { AttendeeMeetingConnection } from './attendee-meeting.connection.entity';
import { PresenterOrHostMeetingAttendee } from './presenter-or-host-attendee-meeting.connection.entity';

const key = 'meetingConnectionId';

export class OrganizationMeetingConnection extends ConnectionEntityAggregate<IConnectionOrganizationDTO, IConnectionAttendeeDTO> {
    public static create(organizationProps: IConnectionOrganizationDTO, organizationRole: OrganizationRole): OrganizationMeetingConnection {
        return new OrganizationMeetingConnection(organizationProps, organizationRole);
    }

    private static createAttendee(role: OrganizationRole, props: IConnectionAttendeeDTO): AttendeeMeetingConnection {
        if (role === OrganizationRole.Hosts || role === OrganizationRole.Presenters || role === OrganizationRole.Supports) {
            return PresenterOrHostMeetingAttendee.create(props);
        }
        return AttendeeMeetingConnection.create(props);
    }

    public getId() {
        return this.getProps().organizationId;
    }

    public toDTO() {
        const _dto = this.getProps();
        _dto.attendees = super.getEntities().map(item => item.toDTO());
        if (this.hasAssignedConnection()) {
            _dto[key] = this.getAssignedConnection();
        } else {
            delete _dto[key];
        }

        return _dto as IConnectionOrganizationDTO;
    }

    @mobx.observable
    private [key]: {};

    private constructor(
        { meetingConnectionId, attendees = [], ...rest }: IConnectionOrganizationDTO,
        private readonly organizationRole: OrganizationRole
    ) {
        super(key, rest);
        if (meetingConnectionId) this.assignConnection(meetingConnectionId);

        attendees.forEach(attendee =>
            super.setEntity(attendee.attendeeId, OrganizationMeetingConnection.createAttendee(organizationRole, attendee))
        );
    }

    public get role(): OrganizationRole {
        return this.organizationRole;
    }

    @mobx.action
    public invalidateAssignedConnectionById(id: number): void {
        super.invalidateAssignedConnectionById(id);
    }

    @mobx.action
    public addAttendee(attendee: IConnectionAttendeeDTO): void {
        super.setEntity(attendee.attendeeId, OrganizationMeetingConnection.createAttendee(this.organizationRole, attendee));
    }
    @mobx.action
    public removeAttendee(attendeeId: number): void {
        super.removeEntity(attendeeId);
    }
    public getAttendee(attendeeId: number): ConnectionEntity<IConnectionAttendeeDTO> {
        return super.getEntity(attendeeId);
    }
}
